import React from 'react';
import { 
  Drawer, 
  Box, 
  Typography, 
  IconButton, 
  Chip, 
  Paper,
  Divider,
  Avatar,
  alpha,
  useTheme,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  ViewInAr as ViewInArIcon,
  Article as ArticleIcon,
  Assessment as AssessmentIcon,
  Map as MapIcon,
  Layers as LayersIcon,
  Search as SearchIcon,
  Grade as GradeIcon,
  Edit as EditIcon,
  CameraAlt as CameraAltIcon,
  Warning as WarningIcon,
  Straighten as StraightenIcon,
} from '@mui/icons-material';
import { ratingApi, assetsApi, default as api } from '../../../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import EditAssetRatingDialog from '../../Assets/EditAssetRatingDialog';
import DisplayImageSelector from '../../Assets/DisplayImageSelector';
import AuthenticatedImage from '../../common/AuthenticatedImage';
import AssetReportsPanel from './AssetReportsPanel';
import AssetDocumentsPanel from './AssetDocumentsPanel';
import SlantingAnalysisModal from '../modals/SlantingAnalysisModal';
 
 // Placeholder for category colors based on seed data
 const categoryColors = {
  'Road Pavement': { main: '#795548', light: '#EFEBE9' }, // Brown
  'Road Furniture': { main: '#ff9800', light: '#FFF3E0' }, // Orange
  'Geotechnical': { main: '#8bc34a', light: '#F1F8E9' }, // Light Green
  'Structural': { main: '#2196f3', light: '#E3F2FD' }, // Blue
  'Vertical Asset': { main: '#7c4dff', light: '#EDE7F6' }, // Deep Purple
  'default': { main: '#9e9e9e', light: '#F5F5F5' }      // Grey (Fallback)
};

/**
 * AssetDetailDrawer Component
 * 
 * Displays the details of a selected asset in a side drawer.
 * 
 * Props:
 *  - open (boolean): Controls the visibility of the drawer.
 *  - onClose (function): Callback function when the close button is clicked.
 *  - asset (object | null): The selected asset object containing details to display.
 *  - onShowInspection (function): Callback function when the inspection button is clicked.
 *  - onShow3DModel (function): Callback function when the 3D model button is clicked.
 *  - is3DModelVisible (boolean): Whether the 3D model is currently visible.
 *  - activeTilesetAsset (object): The currently active 3D model asset.
 *  - isInspectionVisible (boolean): Whether the inspection panel is currently visible.
 *  - inspectionAsset (object): The asset currently being inspected.
 *  - isFootprintVisible (boolean): Whether the asset footprint is currently visible.
 *  - onToggleFootprint (function): Callback function when the footprint button is clicked.
 *  - activeFootprintAsset (object): The asset whose footprint is currently visible.
 */
const AssetDetailDrawer = ({
  open,
  onClose,
  asset,
  onShowInspection,
  onShow3DModel,
  // Add props for toggle state
  is3DModelVisible,
  activeTilesetAsset,
  // Add props for inspection toggle state
  isInspectionVisible = false,
  inspectionAsset = null,
  // Add props for footprint toggle
  isFootprintVisible = false,
  onToggleFootprint,
  activeFootprintAsset = null,
  onVersionsLoaded = () => {},
  onAssetUpdate = () => {}
}) => {
  const theme = useTheme();
  const [gisDataVisible, setGisDataVisible] = React.useState(false);
  const [ratingLevels, setRatingLevels] = React.useState([]);
  const [isRatingEditable, setIsRatingEditable] = React.useState(false);
  const [isRatingDialogOpen, setRatingDialogOpen] = React.useState(false);
  const [inspectionHistory, setInspectionHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [modelVersions, setModelVersions] = React.useState({ latest: null, available: [] });
  const [versionsLoading, setVersionsLoading] = React.useState(false);
  const [displayImageSelectorOpen, setDisplayImageSelectorOpen] = React.useState(false);
  const [reportsPanelOpen, setReportsPanelOpen] = React.useState(false);
  const [documentsPanelOpen, setDocumentsPanelOpen] = React.useState(false);
  const [slantingAnalysisOpen, setSlantingAnalysisOpen] = React.useState(false);

  // Determine if the 3D model for *this specific asset* is currently active
  const isCurrentAsset3DModelActive = React.useMemo(() => {
    if (!asset || !activeTilesetAsset) return false;
    const currentDrawerAssetId = asset.id || asset.assetId;
    const activeTilesetAssetId = activeTilesetAsset.id || activeTilesetAsset.assetId;
    return is3DModelVisible && currentDrawerAssetId === activeTilesetAssetId;
  }, [asset, activeTilesetAsset, is3DModelVisible]);

  // Determine if the inspection panel for *this specific asset* is currently active
  const isCurrentAssetInspectionActive = React.useMemo(() => {
    if (!asset || !inspectionAsset) return false;
    const currentDrawerAssetId = asset.id || asset.assetId;
    const currentInspectionAssetId = inspectionAsset.id || inspectionAsset.assetId;
    return isInspectionVisible && currentDrawerAssetId === currentInspectionAssetId;
  }, [asset, inspectionAsset, isInspectionVisible]);

  // Determine if the footprint for *this specific asset* is currently active
  const isCurrentAssetFootprintActive = React.useMemo(() => {
    if (!asset || !activeFootprintAsset) return false;
    const currentDrawerAssetId = asset.id || asset.assetId;
    const activeFootprintAssetId = activeFootprintAsset.id || activeFootprintAsset.assetId;
    return isFootprintVisible && currentDrawerAssetId === activeFootprintAssetId;
  }, [asset, activeFootprintAsset, isFootprintVisible]);

  React.useEffect(() => {
    const fetchRatingData = async () => {
      if (asset?.organization_id && asset?.asset_type_id) {
        try {
          const response = await ratingApi.getSchemes({
            organization_id: asset.organization_id,
            asset_type_id: asset.asset_type_id,
          });
          const responsePayload = response.data;
          const schemes = Array.isArray(responsePayload)
            ? responsePayload
            : (Array.isArray(responsePayload?.data) ? responsePayload.data : []);

          if (schemes.length > 0) {
            const scheme = schemes[0];
            const levelsResponse = await ratingApi.getLevels({ scheme_id: scheme.id });
            const levelsPayload = levelsResponse.data;
            const levels = Array.isArray(levelsPayload) ? levelsPayload : (Array.isArray(levelsPayload?.data) ? levelsPayload.data : []);
            
            setRatingLevels(levels);
            setIsRatingEditable(!!scheme && !scheme.auto_update_from_inspection);
          } else {
            setRatingLevels([]);
            setIsRatingEditable(false);
          }
        } catch (error) {
          console.error("Failed to fetch rating data:", error);
          setRatingLevels([]);
          setIsRatingEditable(false);
        }
      } else {
        setRatingLevels([]);
        setIsRatingEditable(false);
      }
    };

    if (open && asset) {
      fetchRatingData();
    }
  }, [open, asset]);

  React.useEffect(() => {
    const fetchInspectionHistory = async () => {
      if (asset?.id) {
        setHistoryLoading(true);
        try {
          const response = await api.get(`/inspections/asset/${asset.id}/rating-history`);
          setInspectionHistory(response.data || []);
        } catch (error) {
          console.error("Failed to fetch inspection history:", error);
          setInspectionHistory([]); // Set to empty array on error
        } finally {
          setHistoryLoading(false);
        }
      }
    };

    if (open && asset) {
      fetchInspectionHistory();
    }
  }, [open, asset]);

  React.useEffect(() => {
   const fetchModelVersions = async () => {
     if (asset?.id) {
       setVersionsLoading(true);
       try {
         const response = await assetsApi.getAsset3dModelVersions(asset.id);
         setModelVersions(response.data);
         onVersionsLoaded(response.data); // Pass data up to the parent
         console.log(`Versions for asset ${asset.id}:`, response.data);
       } catch (error) {
         console.error("Failed to fetch 3D model versions:", error);
         onVersionsLoaded({ latest: null, available: [] }); // Pass empty data up on error
         setModelVersions({ latest: null, available: [] });
       } finally {
         setVersionsLoading(false);
       }
     }
   };

   if (open && asset) {
     fetchModelVersions();
   }
  }, [open, asset]);

  // Helper function to get category color safely
  const getCategoryColor = (asset, type = 'main') => {
    const categoryName = asset?.categoryName || asset?.category_name;
    if (categoryName && categoryColors[categoryName]) {
      return categoryColors[categoryName][type];
    }
    return categoryColors.default[type];
  };

  // Gets the fallback display image URL based on the asset's category
  // Returns null if asset has a custom display_image_path (handled by AuthenticatedImage)
  const getDisplayImageForAsset = (asset) => {
    // If asset has a custom display image, we'll use AuthenticatedImage instead
    if (asset?.display_image_path) {
      return null;
    }

    const categoryName = asset?.categoryName || asset?.category_name;
    const cloudfrontUrl = 'https://d39c59lb88xtp6.cloudfront.net/display_image';

    switch (categoryName) {
      case 'Geotechnical':
        return `${cloudfrontUrl}/slope_dp.jpeg`;
      case 'Structural':
        return `${cloudfrontUrl}/bridge_dp.jpeg`;
      default:
        // Fallback for other categories
        return `https://picsum.photos/800/600?random=${asset.id}`;
    }
  };

  // Handler for display image selection
  const handleDisplayImageSelect = (photoId) => {
    // Trigger asset refresh to get updated display_image_path
    if (onAssetUpdate) {
      onAssetUpdate();
    }
  };

  // Handle toggle for the footprint button
  const handleFootprintToggle = () => {
    if (onToggleFootprint && asset) {
      onToggleFootprint(asset);
    }
  };

  const handleGisDataToggle = () => {
    setGisDataVisible(!gisDataVisible);
  };

  // Handler for 3D Model button
  const handle3DModelClick = () => {
    if (onShow3DModel && asset) {
      onShow3DModel(asset);
    }
  };

  // Handler for Inspection button
  const handleInspectionClick = () => {
    if (onShowInspection && asset) {
      onShowInspection(asset);
    }
  };


  const handleEditRating = () => {
    setRatingDialogOpen(true);
  };

  const handleCloseRatingDialog = () => {
    setRatingDialogOpen(false);
  };

  const handleRatingUpdate = () => {
    if (onAssetUpdate) {
      onAssetUpdate();
    }
  };

  return (
   <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '380px',
          maxWidth: '90%',
          boxSizing: 'border-box',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 0 0 16px',
          overflow: 'hidden', // Ensure sticky elements don't overflow
        },
      }}
    >
      {asset ? (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Sticky Banner/Image Area with Close Button */}
          <Box
            sx={{
              height: '240px',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              cursor: 'pointer',
              // Only use backgroundImage for fallback (non-custom images)
              ...(getDisplayImageForAsset(asset) && {
                backgroundImage: `url("${getDisplayImageForAsset(asset)}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }),
              '&:hover .change-image-overlay': {
                opacity: 1,
              },
            }}
            onClick={() => setDisplayImageSelectorOpen(true)}
          >
            {/* Authenticated image for custom display image (prefer thumbnail for faster loading) */}
            {asset.display_image_path && (
              <AuthenticatedImage
                src={asset.display_image_thumbnail_path || asset.display_image_path}
                alt={asset.name || 'Asset image'}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}

            {/* Gradient overlay */}
            <Box sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0.8) 100%)',
              zIndex: 1,
            }}/>

            {/* Change Image hover overlay */}
            <Box
              className="change-image-overlay"
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                bgcolor: 'rgba(0,0,0,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s ease',
                zIndex: 2,
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.6)',
                px: 2,
                py: 1,
                borderRadius: 2,
              }}>
                <CameraAltIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Change Image
                </Typography>
              </Box>
            </Box>

            {/* Close button */}
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              sx={{
                position: 'absolute', top: 16, right: 16,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.4)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                transition: 'all 0.2s ease',
                zIndex: 3
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Asset title at the bottom */}
            <Box sx={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              p: 3, zIndex: 3
            }}>
              <Typography variant="h5" sx={{
                fontWeight: 600,
                color: 'white',
                textShadow: '0px 1px 3px rgba(0,0,0,0.6)',
                mb: 0.5
              }}>
                {asset.name || 'Unnamed Asset'}
              </Typography>

              <Chip
                label={asset.categoryName || asset.category_name || 'Uncategorized'}
                size="small"
                color="primary"
                sx={{
                  height: '24px',
                  fontWeight: 500,
                  bgcolor: getCategoryColor(asset),
                  color: '#fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  mr: 1
                }}
              />

              {asset.status && (
                <Chip
                  label={asset.status}
                  size="small"
                  sx={{
                    height: '24px',
                    fontSize: '0.75rem',
                    bgcolor: asset.status === 'Active' ? alpha(theme.palette.success.main, 0.85) :
                      asset.status === 'Inactive' ? alpha(theme.palette.error.main, 0.85) :
                      asset.status === 'Maintenance' ? alpha(theme.palette.warning.main, 0.85) : 'rgba(255,255,255,0.2)',
                    color: asset.status === 'Active' ? theme.palette.success.contrastText :
                      asset.status === 'Inactive' ? theme.palette.error.contrastText :
                      asset.status === 'Maintenance' ? theme.palette.warning.contrastText : 'white',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                />
              )}

              {asset.is_critical && (
                <Chip
                  icon={<WarningIcon />}
                  label="CRITICAL"
                  size="small"
                  sx={{
                    height: '24px',
                    fontSize: '0.75rem',
                    ml: 1,
                    bgcolor: '#d32f2f',
                    color: '#fff',
                    fontWeight: 600,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    '& .MuiChip-icon': {
                      color: '#fff',
                      fontSize: '1rem'
                    }
                  }}
                />
              )}
            </Box>
          </Box>
          
          {/* Scrollable Content Area */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto',
            height: 'calc(100% - 240px - 140px)', // Subtract sticky header height and sticky footer height
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
            }
          }}>
            {/* ID Card Section */}
            <Box sx={{ 
              p: 3, 
              background: 'white'
            }}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 2,
                mb: 1
              }}>
                <Box sx={{
                  p: 1.5,
                  bgcolor: alpha(getCategoryColor(asset, 'light'), 0.8),
                  borderRadius: 2,
                  border: `1px solid ${alpha(getCategoryColor(asset), 0.1)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: `0 4px 8px ${alpha(getCategoryColor(asset), 0.15)}`
                  }
                }}>
                  <Typography variant="overline" sx={{ color: alpha(getCategoryColor(asset), 0.8), fontSize: '0.7rem', fontWeight: 600 }}>
                    CODE
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: getCategoryColor(asset) }}>
                    {asset.code || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Details Section */}
            <Box sx={{ px: 3, pb: 3, bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CategoryIcon sx={{ fontSize: 18, color: getCategoryColor(asset), mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Classification
                </Typography>
              </Box>
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                rowGap: 1.5,
                columnGap: 1.5,
                mb: 3,
                '& .MuiTypography-root': { lineHeight: 1.5 }
              }}>
                <Typography variant="body2" color="text.secondary">Type:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.type_name || 'N/A'}</Typography>

                <Typography variant="body2" color="text.secondary">Subtype:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.subtypeName || asset.subtype_name || 'N/A'}</Typography>

                <Typography variant="body2" color="text.secondary">Category:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.categoryName || asset.category_name || 'N/A'}</Typography>

                {/* Show Slope Type only for Slope assets */}
                {asset.type_name === 'Slope' && asset.slope_type && (
                  <>
                    <Typography variant="body2" color="text.secondary">Slope Type:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={asset.slope_type === 'C' ? 'Cut' : 'Embankment'}
                        size="small"
                        sx={{
                          height: '22px',
                          fontSize: '0.75rem',
                          bgcolor: asset.slope_type === 'C'
                            ? alpha('#8bc34a', 0.15)
                            : alpha('#ff9800', 0.15),
                          color: asset.slope_type === 'C' ? '#689f38' : '#ef6c00',
                          fontWeight: 500,
                          border: `1px solid ${asset.slope_type === 'C'
                            ? alpha('#8bc34a', 0.3)
                            : alpha('#ff9800', 0.3)}`
                        }}
                      />
                    </Box>
                  </>
                )}

                {/* Show High Mast attributes */}
                {asset.type_name === 'High Mast' && asset.metadata && (
                  <>
                    {asset.metadata.height && (
                      <>
                        <Typography variant="body2" color="text.secondary">Height:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.metadata.height}m</Typography>
                      </>
                    )}
                    {asset.metadata.ring_count && (
                      <>
                        <Typography variant="body2" color="text.secondary">Ring Count:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.metadata.ring_count}</Typography>
                      </>
                    )}
                    {asset.metadata.luminaire_count && (
                      <>
                        <Typography variant="body2" color="text.secondary">Luminaires:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.metadata.luminaire_count}</Typography>
                      </>
                    )}
                    {asset.metadata.pole_material && (
                      <>
                        <Typography variant="body2" color="text.secondary">Pole Material:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.metadata.pole_material}</Typography>
                      </>
                    )}
                    {asset.metadata.manufacturer && (
                      <>
                        <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {asset.metadata.manufacturer}{asset.metadata.model ? ` / ${asset.metadata.model}` : ''}
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </Box>
              
              <Divider sx={{ my: 2, borderColor: alpha(getCategoryColor(asset), 0.2) }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ fontSize: 18, color: getCategoryColor(asset), mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Location
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '120px 1fr', 
                rowGap: 1.5, 
                columnGap: 1.5, 
                mb: 3,
                '& .MuiTypography-root': { lineHeight: 1.5 }
              }}>
                <Typography variant="body2" color="text.secondary">Organization:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.organization_name || 'N/A'}</Typography>

                <Typography variant="body2" color="text.secondary">Network:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.network_name || 'N/A'}</Typography>

                <Typography variant="body2" color="text.secondary">Section:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.section_name || 'N/A'}</Typography>
                
                <Typography variant="body2" color="text.secondary">Coordinates:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {asset.center_lat && asset.center_lng ? 
                    `${Number(asset.center_lat).toFixed(5)}, ${Number(asset.center_lng).toFixed(5)}` : 
                    'N/A'}
                </Typography>
              </Box>
              
              {/* Description */}
              {asset.description && (
                <>
                  <Divider sx={{ my: 2, borderColor: alpha(getCategoryColor(asset), 0.2) }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DescriptionIcon sx={{ fontSize: 18, color: getCategoryColor(asset), mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Description
                    </Typography>
                  </Box>
                  
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    bgcolor: alpha(getCategoryColor(asset, 'light'), 0.6), 
                    borderRadius: 2, 
                    mb: 2,
                    border: `1px solid ${alpha(getCategoryColor(asset), 0.1)}`
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {asset.description}
                    </Typography>
                  </Paper>
                </>
              )}
              
              {/* Technical Data */}
              <Divider sx={{ my: 2, borderColor: alpha(getCategoryColor(asset), 0.2) }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ fontSize: 18, color: getCategoryColor(asset), mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Technical Data
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '120px 1fr', 
                rowGap: 1.5, 
                columnGap: 1.5,
                '& .MuiTypography-root': { lineHeight: 1.5 }
              }}>
                <Typography variant="body2" color="text.secondary">Last Inspection:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {asset.latest_inspection_date ? 
                    new Date(asset.latest_inspection_date).toLocaleDateString('en-GB', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : 
                    'Never'
                  }
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Hazard & Risk Ranking:</Typography>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Current Rating Display as Chip */}
                    {asset.rating_level_id && ratingLevels.length > 0 ? (
                      (() => {
                        const selectedLevel = ratingLevels.find(level => level.id === asset.rating_level_id);
                        return selectedLevel ? (
                          <Chip
                            label={selectedLevel.name.replace('Cx', 'Rx')}
                            size="small"
                            sx={{
                              height: '22px',
                              fontSize: '0.75rem',
                              bgcolor: alpha(selectedLevel.color_code || '#9e9e9e', 0.15),
                              color: selectedLevel.color_code || '#616161',
                              fontWeight: 500,
                              border: `1px solid ${alpha(selectedLevel.color_code || '#9e9e9e', 0.3)}`,
                              '& .MuiChip-label': {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }
                            }}
                            icon={
                              <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: selectedLevel.color_code || '#9e9e9e'
                              }} />
                            }
                          />
                        ) : (
                          <Chip
                            label="N/A"
                            size="small"
                            sx={{
                              height: '22px',
                              fontSize: '0.75rem',
                              bgcolor: alpha('#9e9e9e', 0.1),
                              color: '#616161',
                              fontWeight: 500,
                              border: `1px solid ${alpha('#9e9e9e', 0.2)}`
                            }}
                          />
                        );
                      })()
                    ) : (
                      <Chip
                        label="N/A"
                        size="small"
                        sx={{
                          height: '22px',
                          fontSize: '0.75rem',
                          bgcolor: alpha('#9e9e9e', 0.1),
                          color: '#616161',
                          fontWeight: 500,
                          border: `1px solid ${alpha('#9e9e9e', 0.2)}`
                        }}
                      />
                    )}
                    
                    {isRatingEditable && (
                      <Tooltip title="Edit Manual Rating">
                        <IconButton size="small" onClick={handleEditRating}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Rating History Chart */}
              <Divider sx={{ my: 2, borderColor: alpha(getCategoryColor(asset), 0.2) }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ fontSize: 18, color: getCategoryColor(asset), mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Rating History
                </Typography>
              </Box>

              <Box sx={{ height: 200, mb: 2 }}>
                {historyLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : inspectionHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={inspectionHistory} margin={{ top: 20, right: 30, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.secondary, 0.2)} />
                      <XAxis
                        dataKey="cycle"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        domain={[
                          ratingLevels.length > 0 ? Math.min(...ratingLevels.map(l => l.value)) : 0,
                          ratingLevels.length > 0 ? Math.max(...ratingLevels.map(l => l.value)) : 5
                        ]}
                        ticks={ratingLevels.length > 0 ? ratingLevels.map(l => l.value).sort((a, b) => a - b) : []}
                        tickFormatter={(value) => {
                          const level = ratingLevels.find(l => l.value === value);
                          return level ? level.name : value;
                        }}
                        allowDecimals={false}
                        interval={0}
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            const year = new Date(data.inspection_date).getFullYear();
                            return (
                              <Paper elevation={3} sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(5px)', borderRadius: '8px' }}>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{`Cycle ${label} (${year})`}</Typography>
                                <Typography variant="body2" sx={{ color: data.rating_level_color || theme.palette.text.primary }}>
                                  {`Rating: ${data.rating_level_name} (${data.rating_level_value})`}
                                </Typography>
                              </Paper>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px' }} />
                      <Line
                        type="monotone"
                        dataKey="rating_level_value"
                        name="Rating Value"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ r: 4, fill: theme.palette.primary.main }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                    <AssessmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No rated inspection history available.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          
          {/* Sticky Footer with Buttons */}
          <Box sx={{ 
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 1.5,
            px: 1.5,
            boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
          }}>
            {/* Buttons layout with main on left, secondary on right */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}>
              {/* Main action buttons group - left side */}
              <Box 
                sx={{ 
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'center',
                }}
              >
                {/* Inspection button - primary action, now with toggle state */}
                <Tooltip title={isCurrentAssetInspectionActive ? "Hide Inspection" : "Show Inspection"} arrow placement="top">
                  <ToggleButton
                    value="inspection"
                    selected={isCurrentAssetInspectionActive}
                    onChange={handleInspectionClick}
                    size="small"
                    sx={{
                      p: 1.25,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      borderRadius: '50%',
                      color: theme.palette.action.active,
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        },
                      },
                      '&:not(.Mui-selected):hover': {
                        bgcolor: alpha(theme.palette.grey[200], 0.5),
                      },
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
                
                {/* 3D Model button */}
                {/* 3D Model Toggle Button */}
                <Tooltip title={isCurrentAsset3DModelActive ? "Hide 3D Model" : "Show 3D Model"} arrow placement="top">
                  {/* Use ToggleButton for ON/OFF state */}
                  <ToggleButton
                    value="3d-model"
                    selected={isCurrentAsset3DModelActive}
                    onChange={handle3DModelClick} // Still triggers the same handler in MapViewPage
                    disabled={versionsLoading || modelVersions.available.length === 0}
                    size="small" // Match size with IconButton potentially
                    sx={{
                      p: 1.25, // Keep padding consistent
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      borderRadius: '50%', // Make it circular
                      color: theme.palette.action.active, // Default icon color when OFF
                      // Styling for the SELECTED state (.Mui-selected class)
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main, // Use primary blue when ON
                        color: 'white', // White icon when ON
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark, // Darker blue on hover when ON
                        },
                      },
                      // Default hover style (when NOT selected)
                      '&:not(.Mui-selected):hover': {
                         bgcolor: alpha(theme.palette.grey[200], 0.5), // Default grey hover when OFF
                      },
                    }}
                  >
                    <ViewInArIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
                
                {/* Document button */}
                <Tooltip title="Documents & Files" arrow placement="top">
                  <IconButton
                    onClick={() => setDocumentsPanelOpen(true)}
                    sx={{
                      p: 1.25,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[200], 0.5)
                      }
                    }}
                  >
                    <ArticleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                {/* Report button */}
                <Tooltip title="Asset Reports" arrow placement="top">
                  <IconButton
                    onClick={() => setReportsPanelOpen(true)}
                    sx={{
                      p: 1.25,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.grey[200], 0.5)
                      }
                    }}
                  >
                    <AssessmentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {/* Vertical divider */}
              <Divider 
                orientation="vertical" 
                flexItem 
                sx={{ 
                  mx: 1.5, 
                  height: '30px', 
                  alignSelf: 'center',
                  borderColor: alpha(theme.palette.divider, 0.5)
                }} 
              />
              
              {/* Secondary toggle buttons group - right side */}
              <Box 
                sx={{ 
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'center',
                }}
              >
                {/* Asset Footprint toggle - hidden for point assets (e.g., High Mast) */}
                {!['High Mast', 'Point'].includes(asset?.type_name) && (
                <Tooltip title={isCurrentAssetFootprintActive ? "Hide Asset Footprint" : "Show Asset Footprint"} arrow placement="top">
                  <IconButton
                    color={isCurrentAssetFootprintActive ? "primary" : "default"}
                    onClick={handleFootprintToggle}
                    sx={{
                      p: 1.25,
                      bgcolor: isCurrentAssetFootprintActive ? theme.palette.primary.main : 'transparent',
                      color: isCurrentAssetFootprintActive ? 'white' : theme.palette.text.primary,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      '&:hover': {
                        bgcolor: isCurrentAssetFootprintActive
                          ? theme.palette.primary.dark
                          : alpha(theme.palette.grey[200], 0.5)
                      }
                    }}
                  >
                    <MapIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                )}

                {/* Slanting Analysis button — shown only for point assets (High Mast) */}
                {['High Mast', 'Point'].includes(asset?.type_name) && (
                <Tooltip title="Slanting Analysis" arrow placement="top">
                  <IconButton
                    onClick={() => setSlantingAnalysisOpen(true)}
                    sx={{
                      p: 1.25,
                      bgcolor: 'transparent',
                      color: '#7c4dff',
                      border: `1px solid ${alpha('#7c4dff', 0.3)}`,
                      '&:hover': {
                        bgcolor: alpha('#7c4dff', 0.08)
                      }
                    }}
                  >
                    <StraightenIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
                )}

                {/* GIS Data toggle */}
                <Tooltip title="GIS Data" arrow placement="top">
                  <IconButton
                    color={gisDataVisible ? "primary" : "default"}
                    onClick={handleGisDataToggle}
                    sx={{ 
                      p: 1.25,
                      bgcolor: gisDataVisible ? theme.palette.primary.main : 'transparent',
                      color: gisDataVisible ? 'white' : theme.palette.text.primary,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                      '&:hover': {
                        bgcolor: gisDataVisible 
                          ? theme.palette.primary.dark
                          : alpha(theme.palette.grey[200], 0.5)
                      }
                    }}
                  >
                    <LayersIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        // Empty state when no asset is selected
        <Box sx={{ 
          p: 4, 
          textAlign: 'center', 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 2,
          height: '100%',
          bgcolor: 'white'
        }}>
          <Avatar sx={{ 
            width: 80, 
            height: 80, 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            mb: 2
          }}>
            <InfoIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          </Avatar>
          
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            No asset selected
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80%', mb: 2 }}>
            Click on any point on the map to view detailed information about the asset
          </Typography>
          
          <Box sx={{
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
            width: '80%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AssignmentIcon sx={{ fontSize: 16, color: theme.palette.primary.main, mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                Quick Tips
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left', pl: 3.5, mt: 0.5 }}>
              • Click on blue markers to view assets
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left', pl: 3.5, mt: 0.5 }}>
              • Use the filters to narrow down results
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left', pl: 3.5, mt: 0.5 }}>
              • Zoom in to see more detailed assets
            </Typography>
          </Box>
        </Box>
      )}
    </Drawer>
     {asset && (
       <>
         <EditAssetRatingDialog
           open={isRatingDialogOpen}
           onClose={handleCloseRatingDialog}
           asset={asset}
           onUpdate={handleRatingUpdate}
         />
         <DisplayImageSelector
           open={displayImageSelectorOpen}
           onClose={() => setDisplayImageSelectorOpen(false)}
           asset={asset}
           currentPhotoId={asset.display_image_photo_id}
           onSelect={handleDisplayImageSelect}
         />
         <AssetReportsPanel
           open={reportsPanelOpen}
           onClose={() => setReportsPanelOpen(false)}
           asset={asset}
         />
         <AssetDocumentsPanel
           open={documentsPanelOpen}
           onClose={() => setDocumentsPanelOpen(false)}
           asset={asset}
         />
         <SlantingAnalysisModal
           open={slantingAnalysisOpen}
           onClose={() => setSlantingAnalysisOpen(false)}
           asset={asset}
         />
       </>
     )}
   </>
  );
};

export default AssetDetailDrawer;